import axios from 'axios';

// ?? (not ||): the demo build sets VITE_API_URL='' so the base is SAME-ORIGIN
// ('' + /api/marketing, proxied by nginx). '' is falsy, so || would wrongly
// fall back to localhost. Unset (dev) still yields http://localhost:8080.
const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8080').trim();

const api = axios.create({
  baseURL: `${API_URL}/api/marketing`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('marketing_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('marketing_token');
      localStorage.removeItem('marketing_signup_id');
      localStorage.removeItem('marketing_email');
      localStorage.removeItem('marketing_company');
      localStorage.removeItem('marketing_status');
      localStorage.removeItem('marketing_email_verified');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface SignupRequest {
  email: string;
  password: string;
  companyName: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactPhone?: string;
  captchaToken: string;
}

export interface SignupResponse {
  token: string;
  signupId: string;
  email: string;
  companyName: string;
}

export interface RequestDemoRequest {
  email: string;
  companyName?: string;
  contactName?: string;
  phone?: string;
}

export interface RequestDemoResponse {
  ok: boolean;
  // snake_case: returned verbatim by the backend demo_lead handler
  redirect_url: string;
  access_token?: string;
  refresh_token?: string;
  csrf_token?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  captchaToken: string;
}

export interface LoginResponse {
  token: string;
  signupId: string;
  email: string;
  companyName: string;
  status: string;
  emailVerified: boolean;
}

export interface IntakeData {
  // Business Information
  legalName?: string;
  dbaName?: string;
  businessType?: 'collection_agency' | 'law_firm' | 'creditor' | 'healthcare' | 'utility' | 'other';
  taxId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  businessPhone?: string;
  website?: string;

  // Compliance Details
  operatingStates?: string[];
  stateLicenses?: Array<{
    state: string;
    licenseNumber: string;
    expiryDate?: string;
  }>;
  bondingInfo?: {
    isBonded: boolean;
    amount?: number;
    provider?: string;
    policyNumber?: string;
  };
  complianceOfficerName?: string;
  complianceOfficerEmail?: string;
  complianceOfficerPhone?: string;

  // Integration Preferences
  featuresRequested?: {
    voip?: boolean;
    email?: boolean;
    sms?: boolean;
    creditBureau?: boolean;
    skipTracing?: boolean;
  };
  estimatedUsers?: number;
  estimatedAccounts?: number;
  dataMigrationNeeded?: boolean;
  currentSoftware?: string;

  // Phone Configuration
  phoneProvider?: 'twilio' | 'telnyx' | 'custom_sip' | 'manual' | 'none';
  phoneConfig?: {
    accountSid?: string;
    authToken?: string;
    apiKey?: string;
    sipHost?: string;
    sipPort?: number;
    sipUsername?: string;
    sipPassword?: string;
  };
  numbersToPort?: string[];

  // Preferred subdomain
  preferredSubdomain?: string;

  // Progress tracking
  currentStep?: number;
}

export interface IntakeResponse {
  intake: Partial<IntakeData>;
  currentStep: number;
  completed: boolean;
}

export interface SubdomainCheckResponse {
  available: boolean;
  subdomain: string;
  suggestion?: string;
}

export interface StatusResponse {
  status: string;
  emailVerified: boolean;
  intakeCompleted: boolean;
  paymentCompleted: boolean;
  instanceStatus?: string;
  subdomain?: string;
}

export interface CheckoutResponse {
  checkoutUrl: string;
  sessionId: string;
}

export const marketingApi = {
  // Public investor demo: capture a lead and (in demo mode) get a session +
  // redirect URL to drop the visitor straight into the read-only demo app.
  async requestDemo(data: RequestDemoRequest): Promise<RequestDemoResponse> {
    const response = await api.post('/request-demo', {
      email: data.email,
      company_name: data.companyName,
      contact_name: data.contactName,
      phone: data.phone,
    });
    return response.data;
  },

  // Authentication
  async signup(data: SignupRequest): Promise<SignupResponse> {
    const response = await api.post('/signup', data);
    return response.data;
  },

  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/login', data);
    return response.data;
  },

  async verifyEmail(token: string): Promise<void> {
    await api.post('/verify-email', { token });
  },

  async resendVerification(email: string): Promise<void> {
    await api.post('/resend-verification', { email });
  },

  // Status
  async getStatus(): Promise<StatusResponse> {
    const response = await api.get('/status');
    return response.data;
  },

  // Intake
  async getIntake(): Promise<IntakeResponse> {
    const response = await api.get('/intake');
    return response.data;
  },

  async saveIntake(data: Partial<IntakeData>): Promise<IntakeData> {
    const response = await api.post('/intake', data);
    return response.data;
  },

  async submitIntake(data: IntakeData): Promise<void> {
    await api.post('/intake/complete', data);
  },

  // Subdomain
  async checkSubdomain(subdomain: string): Promise<SubdomainCheckResponse> {
    const response = await api.get(`/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`);
    return response.data;
  },

  // Subscription
  async createCheckout(seatCount: number, billingCycle: 'monthly' | 'annual'): Promise<CheckoutResponse> {
    const response = await api.post('/subscription/checkout', { seatCount, billingCycle });
    return response.data;
  },

  async getSubscriptionStatus(): Promise<{
    status: string;
    seatCount: number;
    pricePerSeat: number;
    billingCycle: string;
    currentPeriodEnd: string;
    trialEndsAt?: string;
  }> {
    const response = await api.get('/subscription/status');
    return response.data;
  },

  // Instance
  async getInstanceStatus(): Promise<{
    status: string;
    subdomain: string;
    provisionedAt?: string;
    activatedAt?: string;
  }> {
    const response = await api.get('/instance/status');
    return response.data;
  },
};

export default marketingApi;
